import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Status } from '../configs/status';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class FlowService {
  FLOW_DEFAULT_PERSONA = `/personas`;
  FLOW_DEFAULT_GROUP = `/groups`;
  FLOW_DEFAULT_BASE_PATH = `/flows-default`;
  FLOW_VALIDATE_BASE_PATH = `/flows-validate`;
  COMPANY_PATH = `/companies`;
  FLOW_JOURNEY = `/flows-journeys`;
  FLOW = `/flows`;

  constructor(private http: HttpService) {}

  getFlows(params: object = {}, url): Observable<any> {
    return this.http.get(`${url}`, params);
  }

  getFlowById(id, params, url): Observable<any> {
    return this.http.get(`${url}/${id}`, params);
  }

  getCompanies(params: object = {}): Observable<any> {
    return this.http.get(`${this.COMPANY_PATH}`, params);
  }

  upsertFlowInfo(body: object = {}, url): Observable<any> {
    return this.http.put(`${url}`, body);
  }

  upsertJourneyPoint(body: object = {}, id, url): Observable<any> {
    return this.http.put(`${url}/${id}/steps`, body);
  }

  deleteFlow(id: number, url): Observable<any> {
    return this.http.delete(`${url}/${id}`);
  }

  activeFlow(flowId: number, url): Observable<any> {
    return this.http.put(`${url}`, {
      id: flowId,
      status: Status.FlowStatus.Active,
    });
  }

  inactiveFlow(flowId: number, url): Observable<any> {
    return this.http.put(`${url}`, {
      id: flowId,
      status: Status.FlowStatus.Inactive,
    });
  }

  updateFlowPersona(id: number, body: object = {}, url): Observable<any> {
    return this.http.put(`${url}/${id}${this.FLOW_DEFAULT_PERSONA}`, body);
  }

  updateFlowGroup(id: number, body: object = {}, url): Observable<any> {
    return this.http.put(`${url}/${id}${this.FLOW_DEFAULT_GROUP}`, body);
  }

  upsertActions(id, body: object = {}, stepId, url): Observable<any> {
    return this.http.put(`${url}/${id}/steps/${stepId}`, body);
  }

  getFlowsDefault(params: object = {}): Observable<any> {
    return this.http.get(`${this.FLOW_DEFAULT_BASE_PATH}`, params);
  }

  getFlowsValidate(params: object = {}): Observable<any> {
    return this.http.get(`${this.FLOW_VALIDATE_BASE_PATH}`, params);
  }

  helpFlowValidationNoLocation(id, personaID, body): Observable<any> {
    return this.http.patch(`${this.FLOW_VALIDATE_BASE_PATH}/${id}/validation/${personaID}`, body);
  }

  updateFlowValidation(body, id, url, flowPersonaId?): Observable<any> {
    let endpoint = `${url}/${id}`;
    if(flowPersonaId) {
      endpoint += `/validation/${flowPersonaId}`;
    }
    return this.http.patch(endpoint, body);
  }

  updateStepFlowValidation(
    id,
    body: object = {},
    stepId,
    url
  ): Observable<any> {
    return this.http.patch(`${url}/${id}/steps/${stepId}`, body);
  }

  updateFlowValidationSteps(
    id,
    body: any[] = [],
    url
  ): Observable<any> {
    return this.http.patch(`${url}/${id}/steps`, body);
  }



  getFlowsJourneys(params: object = {}): Observable<any> {
    return this.http.get(`${this.FLOW_JOURNEY}`, params);
  }

  getFlowsJourneyById(id, params): Observable<any> {
    return this.http.get(`${this.FLOW_JOURNEY}/${id}`, params);
  }

  updateFlowJourneyExpectedDates(id, body): Observable<any> {
    return this.http.patch(`${this.FLOW_JOURNEY}/${id}/expected_dates`, body);
  }

  updateFlowJourneyContainer(id, body): Observable<any> {
    return this.http.patch(`${this.FLOW_JOURNEY}/${id}/container_no`, body);
  }

  getPersonaById(flowId: number, url): Observable<any> {
    return this.http.get(`${url}/${flowId}/personas`, { sort: 'name ASC' });
  }

  rejectFlowJourney(id, body): Observable<any> {
    return this.http.patch(`${this.FLOW_JOURNEY}/${id}`, body);
  }

  search(params?): Observable<any> {
    return this.http.get(`${this.FLOW_JOURNEY}/search`, params);
  }

  report(params?): Observable<any> {
    return this.http.get(`${this.FLOW_JOURNEY}/report`, params);
  }

  reportPDF(params?): Observable<any> {
    return this.http.get(`${this.FLOW_JOURNEY}/report/pdf`, params, { responseType: 'blob' });
  }

  reportMaster(params?): Observable<any> {
    return this.http.get(`${this.FLOW_JOURNEY}/report-master`, params);
  }

  reportMasterXLS(params?): Observable<any> {
    return this.http.get(`${this.FLOW_JOURNEY}/report-master/xls`, params, { responseType: 'blob' });
  }

  cancelContainer(id: number, body): Observable<any> {
    return this.http.patch(`${this.FLOW_JOURNEY}/${id}/container_no/cancel`, body);
  }

  checkExistContainerNo(id: number, containerNo: string) {
    return this.http.get(`${this.FLOW_VALIDATE_BASE_PATH}/${id}/exists/${containerNo}`);
  }

  patchFlowNotification(id: number, body?: any): Observable<any> {
    return this.http.patch(`${this.FLOW}/${id}/notification/toggle`, body);
  }

  patchFlowActionNotification(actionId: number, body?: any) {
    return this.http.patch(`${this.FLOW_JOURNEY}/${actionId}/flow_action_notification/toggle`, body)
  }

  patchFlowCategory(id: number, body?: any): Observable<any> {
    return this.http.patch(`${this.FLOW_JOURNEY}/${id}/categories`, body);
  }
}
